/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  FormState,
  FormStateBuilder,
  whenUsOrAuRegion,
} from '@gh/builder-shared';
import {
  ActionFlow,
  AppRouter,
  DateTimeTypes,
  getRouteInitData,
  MaskedTypes,
  NumberTypes,
  TextDictionary,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { REGION_CODE } from '@gh/config';
import {
  AddressAutocompleteComponent,
  AddressComponents,
  FormMode,
  setControlValue,
} from '@gh/core-ui';
import { assign } from 'lodash';
import { MasterZoneDetailsBusinessService } from '../../resources/master-zone-details-business.service';
import { MasterZoneDetails } from '../../resources/master-zone-details.model';
import { ValidatorFn, Validators } from '@angular/forms';
import { createEnum } from '@gh/core-data';

const MONTH_IN_YEAR = 12;
const MONTH = createEnum('MONTH', {
  18: 18,
  24: 24,
  36: 36,
});
createEnum('LANGUAGE', {
  en_US: 'EN_US',
  en_AU: 'EN_AU',
  en_NZ: 'EN_NZ',
});
const PERCENT = 100;

@Component({
  standalone: false,
  selector: 'gh-master-zone-details',
  templateUrl: 'master-zone-details.component.html',
  styleUrls: ['master-zone-details.component.scss'],
})
export class MasterZoneDetailsComponent extends AbstractFormComponent<MasterZoneDetails> implements OnInit, AfterViewInit {
  @ViewChild(AddressAutocompleteComponent) addressAutocomplete: AddressAutocompleteComponent | undefined;

  states: TextDictionary;
  creationMode: boolean = false;
  form: FormState<MasterZoneDetails>;
  timeZones: TextDictionary;
  showManualAddressFields = false;
  private _currentTab: string = 'common';

  constructor(private router: AppRouter,
    private route: ActivatedRoute,
    private businessService: MasterZoneDetailsBusinessService,
    private navigationService: BuilderNavigationService,
    private formBuilder: FormStateBuilder,
    private pipelines: FormActionPipelines,
    private commonActions: CommonActions) {
    super();
  }

  get completeFlow(): ActionFlow<FormFlowState<MasterZoneDetails>> {
    if (this.form.group.controls['billing'].invalid) {
      this.onTabChanged('billing');
      setTimeout(() => {
        this.form.group.markAllAsTouched();
        this.form.get('billing').markAllAsTouched();
      });
    } else {
      if (this.form.group.invalid) {
        this.onTabChanged('common');
        this.checkAndShowManualAddressFields();
        setTimeout(() => this.form.group.markAllAsTouched());
      }
    }
    return this.pipelines.complete({
      execute: this.commonActions.execute((state) => {
        if (this.creationMode) {
          return this.businessService.create(<any>state.value);
        } else {
          return this.businessService.update(<any>state.value);
        }
      }),
      finish: (state: FormFlowState<MasterZoneDetails>) => {
        const { value, form } = state;
        if (this.creationMode) {
          this.router.switchTo(['/master-zones', value.id]);
        } else {
          (<FormState<MasterZoneDetails>>form).setValue(value);
        }
      },
    });
  }

  get currentTab(): string {
    return this._currentTab;
  }

  minDate(type: string): Date {
    return this.form.get(type)?.value;
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const { masterZone, ...init } = getRouteInitData(this.route);
    assign(this, init);

    this.form = this.formBuilder.buildFunctional(masterZone, (value, { root, group }) => root({
      mode: this.creationMode ? FormMode.CreateMode : FormMode.EditMode,
      optimisticLock: true,
      children: {
        id: void 0,
        name: { required: true },
        code: { required: true, type: MaskedTypes.MasterZoneAbbr },

        companyName: void 0,
        businessType: void 0,
        formedDate: { type: DateTimeTypes.ShortDate },
        taxId: { type: MaskedTypes.TaxId },
        acn: { type: MaskedTypes.Acn },
        tradingAs: void 0,
        street1: void 0,
        street2: void 0,
        city: void 0,
        state: { required: whenUsOrAuRegion() },
        postcode: { type: MaskedTypes.Zip },
        phone1: void 0,
        phone2: void 0,
        fax: void 0,
        abn: { type: MaskedTypes.Abn },
        gst: { type: MaskedTypes.Gst },
        active: void 0,
        timeZone: { enabled: this.creationMode },
        billing: group({
          children: {
            contractSignedDate: { required: true, type: DateTimeTypes.ShortDate },
            depositDate: { required: true, type: DateTimeTypes.ShortDate },
            commencementDate: { required: true, type: DateTimeTypes.ShortDate },
            term: { required: true, type: NumberTypes.Quantity },
            renewalDate: {
              required: true, type: DateTimeTypes.ShortDate, validators: [
                Validators.required,
                this.renewalDateValidator(value),
              ],
            },
            closedDate: { type: DateTimeTypes.ShortDate },
            taxRate: { required: true, type: NumberTypes.Percents },
            initialFranchiseFee: { type: NumberTypes.SignedPrice },
            franchiseFee: { required: true, type: NumberTypes.Percents },
            variationFee: { type: NumberTypes.Percents },
            within18Months: { type: NumberTypes.Percents },
            within24Months: { type: NumberTypes.Percents },
            within36Months: { type: NumberTypes.Percents },
            after36Months: { type: NumberTypes.Percents },
            onSellDate: { type: DateTimeTypes.ShortDate },
            saleAmount: { type: NumberTypes.SignedPrice },
            assignmentFee: { type: NumberTypes.SignedPrice },
            pastMaster: void 0,
            originalAgreementDate: { type: DateTimeTypes.ShortDate },
            saleFee: { required: true, type: NumberTypes.Percents },
            saleFeeAmount: { required: true, type: NumberTypes.SignedPrice },
          },
        }),
      },
    }));
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.onAddressChange();

      const hasAddressData = !!(this.form.group.get('street1')?.value ||
        this.form.group.get('street2')?.value ||
        this.form.group.get('city')?.value ||
        this.form.group.get('state')?.value ||
        this.form.group.get('postcode')?.value);
      this.showManualAddressFields = hasAddressData;
    });
  }

  renewalDateValidator(form: any): ValidatorFn {
    return (control): { [key: string]: any } | null => {
      const value = control.value;
      const commencementDate = this.form ? this.form.get('billing.commencementDate').value : form.billing.commencementDate;
      return value <= commencementDate ? { pattern: { messageId: 'msg_incorrect_master_renewal_date' } } : null;
    };
  }
  checkRegion(regionCode: string): boolean {
    return regionCode === REGION_CODE;
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'MASTER_ZONE',
      label: label ? label : 'lbl_menu_master_zones',
    });
  }

  onTabChanged(name: string): void {
    if (this._currentTab !== name) {
      this._currentTab = name;
    }
  }

  onRecalculateAssignmentFee(): void {
    const onSellDate = this.form.get('billing.onSellDate').value;
    const contractSignedDate = this.form.get('billing.contractSignedDate').value;
    const saleAmount = this.form.get('billing.saleAmount').value;

    const within18Months = this.form.get('billing.within18Months').value;
    const within24Months = this.form.get('billing.within24Months').value;
    const within36Months = this.form.get('billing.within36Months').value;
    const after36Months = this.form.get('billing.after36Months').value;

    const monthsBtwnDates = (startDate: Date, endDate: Date) => {
      if (!startDate || !endDate) {
        return null;
      }
      startDate = new Date(startDate);
      endDate = new Date(endDate);
      return Math.abs(
        (endDate.getFullYear() - startDate.getFullYear()) * MONTH_IN_YEAR +
        endDate.getMonth() -
        startDate.getMonth());
    };

    const months = monthsBtwnDates(onSellDate, contractSignedDate);

    if (!months) {
      setControlValue(this.form.group, 'billing.assignmentFee', null);
    } else {
      if (months <= MONTH['18']) {
        setControlValue(this.form.group, 'billing.assignmentFee', saleAmount * within18Months / PERCENT);
      } else {
        if (months <= MONTH['24']) {
          setControlValue(this.form.group, 'billing.assignmentFee', saleAmount * within24Months / PERCENT);
        } else {
          if (months <= MONTH['36']) {
            setControlValue(this.form.group, 'billing.assignmentFee', saleAmount * within36Months / PERCENT);
          } else {
            if (months > MONTH['36']) {
              setControlValue(this.form.group, 'billing.assignmentFee', saleAmount * after36Months / PERCENT);
            }
          }
        }
      }
    }
  }

  onRecalculateRenewalDate(): void {
    const term = this.form.get('billing.term').value;
    const commencementDate = this.form.get('billing.commencementDate').value;
    setControlValue(this.form.group, 'billing.renewalDate',
      new Date(commencementDate.getFullYear() + term, commencementDate.getMonth(), commencementDate.getDate()));
  }

  onAddressChange() {
    const street1 = this.form.group.get('street1')?.value || '';
    const street2 = this.form.group.get('street2')?.value || '';
    const city = this.form.group.get('city')?.value || '';
    const state = this.form.group.get('state')?.value || '';
    const postCode = this.form.group.get('postcode')?.value || '';

    const addressParts: string[] = [];

    const streetCombined = [street2, street1].filter(Boolean).join(' ');
    if (streetCombined) addressParts.push(streetCombined);

    if (city) addressParts.push(city);
    if (state) addressParts.push(state);
    if (postCode) addressParts.push(postCode);

    const newAddress = addressParts.join(', ');

    if (this.addressAutocomplete) {
      this.addressAutocomplete.inputValue = newAddress;
    }
  }

  onAddressSelected(components: AddressComponents): void {
    const streetParts: string[] = [];
    if (components.streetNo) streetParts.push(components.streetNo);
    if (components.street) streetParts.push(components.street);
    const street1Value = streetParts.join(' ').trim();

    this.form.group.patchValue({
      street1: street1Value || '',
      city: components.city || '',
      state: components.state || '',
      postcode: components.postalCode || ''
    });

    ['street1', 'city', 'state', 'postcode'].forEach(field => {
      const control = this.form.group.get(field);
      if (control) control.markAsDirty();
    });
  }

  private checkAndShowManualAddressFields(): void {
    if (this.showManualAddressFields) return;

    const addressFields = ['street1', 'city', 'state', 'postcode'];
    const hasAddressErrors = addressFields.some(field => {
      const control = this.form.group.get(field);
      return control && control.invalid;
    });

    if (hasAddressErrors) {
      this.showManualAddressFields = true;
    }
  }

}
